/**
 * Authenticated CSV export of Shopify sales. The route needs the bearer header,
 * so the file is fetched as a blob and saved through a transient object URL.
 */
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { ShopifyConversionsRange } from '@discovr/types';
import { createShopifyQueriesApi } from '../api/shopifyQueries';
import { shopifyCopy } from '../../copy';

const api = createShopifyQueriesApi();

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Data records after the header; a newline inside a quoted field does not start a record. */
export function countCsvRecords(text: string): number {
  let records = 0;
  let inQuotes = false;
  let hasContent = false;
  for (const ch of text) {
    if (ch === '"') {
      inQuotes = !inQuotes;
      hasContent = true;
    } else if ((ch === '\n' || ch === '\r') && !inQuotes) {
      if (hasContent) records++;
      hasContent = false;
    } else if (ch.trim() !== '') {
      hasContent = true;
    }
  }
  if (hasContent) records++;
  return Math.max(records - 1, 0);
}

const countCsvRows = async (blob: Blob): Promise<number> => countCsvRecords(await blob.text());

/** `download(campaignId, range?)` saves one campaign's sales (optionally one date range);
 *  `download()` saves every campaign of the store, all time. */
export function useShopifySalesExport() {
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);

  const download = useCallback(async (campaignId?: string, range?: ShopifyConversionsRange): Promise<boolean> => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setPending(true);
    try {
      const { blob, filename } = await api.exportConversions(campaignId, range);
      const rows = await countCsvRows(blob).catch(() => -1);
      if (rows === 0) {
        toast.info(range?.from || range?.to ? shopifyCopy.report.exportEmpty : shopifyCopy.report.exportEmptyAllTime);
        return true;
      }
      saveBlob(blob, filename);
      if (rows > 0) toast.success(shopifyCopy.report.exportDone(rows));
      return true;
    } catch {
      // A blob response hides the JSON error body, so the client message is always the generic axios text.
      toast.error(shopifyCopy.report.exportFailed);
      return false;
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }, []);

  return { download, pending };
}
